import { Link } from 'react-router-dom';
import { useState, useCallback, useRef } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

function BrandIntroSection() {
  return (
    <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-50">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10">
          <div className="w-16 h-16 md:w-18 md:h-18 flex items-center justify-center rounded-2xl bg-primary-100 mx-auto mb-5">
            <i className="ri-compass-3-line text-3xl text-primary-600"></i>
          </div>
          <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
            关于最新网址导航
          </h1>
          <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
            致力于为用户提供最全面、最准确、最实时的网址导航服务
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-4">
              我们的使命
            </h2>
            <p className="text-sm text-foreground-700 leading-relaxed mb-4">
              在互联网信息爆炸的时代，各类平台的网址可能因域名更换、服务升级等原因频繁变更。用户常常因为找不到正确的官方入口而浪费时间，甚至误入仿冒网站，面临账号安全和隐私泄露的风险。
            </p>
            <p className="text-sm text-foreground-700 leading-relaxed mb-4">
              最新网址导航应运而生。我们汇聚各大平台官方最新网址入口，通过实时监控和人工审核相结合的方式，确保每一个收录网址都是安全、有效、官方的。无论您需要访问搜索引擎、社交媒体、视频平台、购物网站，还是政府服务平台，在这里都能找到最新网址入口。
            </p>
            <p className="text-sm text-foreground-700 leading-relaxed">
              我们的愿景是成为中国用户最信赖的网址导航平台，让每一个人都能快速、安全地访问互联网上的每一个角落。
            </p>
          </div>

          <div>
            <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-4">
              核心优势
            </h2>
            <div className="flex flex-col gap-3">
              {[
                { icon: 'ri-shield-check-line', title: '安全可靠', desc: '所有网址均经过人工审核验证，确保为官方认证入口，杜绝仿冒网站和钓鱼链接。' },
                { icon: 'ri-refresh-line', title: '实时更新', desc: '编辑团队每日监控各平台网址变化，24小时内完成更新，确保您获取的都是最新网址。' },
                { icon: 'ri-layout-grid-line', title: '分类清晰', desc: '八大详细分类覆盖主流互联网平台，快速定位您需要的最新网址入口，一目了然。' },
                { icon: 'ri-heart-line', title: '完全免费', desc: '最新网址导航永久免费向所有用户开放，无广告干扰，提供纯净的网址查询体验。' },
                { icon: 'ri-smartphone-line', title: '多端适配', desc: '网站采用响应式设计，支持电脑、平板、手机等多种设备，随时随地查询最新网址。' },
                { icon: 'ri-customer-service-2-line', title: '用户至上', desc: '重视每一位用户的反馈，及时处理失效链接报告，持续优化网址资源质量。' },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-3 p-4 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
                    <i className={`${item.icon} text-accent-600 text-base`}></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-heading font-semibold text-foreground-950 mb-0.5">{item.title}</h3>
                    <p className="text-xs text-foreground-600 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ReputationSection() {
  return (
    <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10">
          <p className="text-xs md:text-sm font-medium text-primary-600 mb-2 uppercase tracking-wide">信誉展示</p>
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
            值得信赖的网址导航平台
          </h2>
          <p className="text-sm text-foreground-600 max-w-xl mx-auto">
            成立以来，我们始终坚持以用户需求为中心，赢得了广大用户的信赖和好评
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4 mb-10">
          {[
            { value: '50万+', label: '累计访问用户' },
            { value: '100+', label: '收录平台数量' },
            { value: '99.9%', label: '网址可用率' },
            { value: '24h', label: '信息更新速度' },
            { value: '5星', label: '用户满意度' },
          ].map((stat) => (
            <div key={stat.label} className="p-4 md:p-5 rounded-xl bg-background-50 border border-background-200/70 text-center">
              <p className="text-xl md:text-2xl font-heading font-bold text-primary-600 mb-1">{stat.value}</p>
              <p className="text-xs text-foreground-500">{stat.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              name: '张先生',
              role: '互联网从业者',
              comment: '非常实用的网址导航网站！之前经常找不到某些平台的官方入口，现在直接在这里就能查到最新网址，太方便了。',
              rating: 5,
            },
            {
              name: '李女士',
              role: '企业行政人员',
              comment: '网址更新速度很快，发现了失效链接反馈后第二天就修复了。客服响应也很及时，推荐给同事了。',
              rating: 5,
            },
            {
              name: '王同学',
              role: '大学生',
              comment: '分类很清晰，学习资源类的网址特别全，中国大学MOOC和学堂在线的入口都是在这里找到的，一直在用。',
              rating: 5,
            },
          ].map((review) => (
            <div key={review.name} className="p-5 rounded-xl bg-background-50 border border-background-200/70">
              <div className="flex items-center gap-1 mb-3">
                {Array.from({ length: review.rating }).map((_, i) => (
                  <i key={i} className="ri-star-fill text-primary-500 text-sm"></i>
                ))}
              </div>
              <p className="text-sm text-foreground-700 leading-relaxed mb-4">{review.comment}</p>
              <div>
                <p className="text-sm font-medium text-foreground-950">{review.name}</p>
                <p className="text-xs text-foreground-500">{review.role}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ContactSection() {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'message' && value.length > 500) return;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="company_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setStatus('success');
      setStatusMessage('感谢您的留言！我们会尽快回复您。');
      return;
    }

    const fd = new FormData(form);
    const url = 'https://readdy.ai/api/form/d9u561f8l97n3eas3hk0';

    try {
      const params = new URLSearchParams();
      fd.forEach((value, key) => {
        params.append(key, value as string);
      });
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed && parsed.code === 'OK') {
        setStatus('success');
        setStatusMessage('感谢您的留言！我们会尽快回复您。');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '提交失败，请稍后重试';
        setStatus('error');
        setStatusMessage(serverMsg);
      }
    } catch {
      setStatus('error');
      setStatusMessage('网络错误，请检查网络连接后重试');
    }
  }, []);

  return (
    <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10">
          <p className="text-xs md:text-sm font-medium text-accent-600 mb-2 uppercase tracking-wide">联系我们</p>
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
            与我们取得联系
          </h2>
          <p className="text-sm text-foreground-600 max-w-xl mx-auto">
            如果您有网址推荐、失效链接反馈或任何建议，欢迎通过以下方式联系我们
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1 flex flex-col gap-4">
            {[
              { icon: 'ri-mail-line', title: '电子邮箱', content: 'contact@latest-url-nav.com', desc: '商务合作及问题反馈' },
              { icon: 'ri-time-line', title: '工作时间', content: '周一至周五 9:00-18:00', desc: '法定节假日休息' },
              { icon: 'ri-map-pin-line', title: '办公地址', content: '北京市朝阳区互联网科技园', desc: '欢迎来访交流' },
            ].map((item) => (
              <div key={item.title} className="flex items-start gap-3 p-4 rounded-xl bg-background-50 border border-background-200/70">
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                  <i className={`${item.icon} text-primary-600 text-base`}></i>
                </div>
                <div>
                  <h3 className="text-sm font-heading font-semibold text-foreground-950 mb-0.5">{item.title}</h3>
                  <p className="text-sm text-foreground-700 font-medium">{item.content}</p>
                  <p className="text-xs text-foreground-500 mt-0.5">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="lg:col-span-2">
            <form ref={formRef} data-readdy-form="" onSubmit={handleSubmit} className="p-6 rounded-xl bg-background-50 border border-background-200/70">
              <h3 className="text-base font-heading font-semibold text-foreground-950 mb-5">发送消息</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="请输入您的姓名"
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="请输入您的邮箱"
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  />
                </div>
              </div>
              <div className="mb-4">
                <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                <input
                  id="contact-subject"
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  placeholder="请输入消息主题"
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                />
              </div>
              <div className="mb-4">
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容 <span className="text-foreground-400 font-normal">({formData.message.length}/500)</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  placeholder="请输入您的留言内容，不超过500字"
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                />
              </div>
              <input
                type="text"
                name="company_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="honeypot-field"
              />
              <button
                type="submit"
                className="w-full sm:w-auto px-8 py-2.5 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-send-plane-line mr-2"></i>发送消息
              </button>
              {status === 'success' && (
                <p className="mt-3 text-sm text-accent-600">{statusMessage}</p>
              )}
              {status === 'error' && (
                <p className="mt-3 text-sm text-red-500">{statusMessage}</p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function AboutPage() {
  const orgSchema = {
    name: '最新网址导航',
    url: 'https://navlink2023.github.io/',
    description: '最新网址导航 - 实时收录各大平台官方最新网址入口',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@latest-url-nav.com',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京市',
      addressCountry: 'CN',
    },
  };

  const breadcrumbSchema = {
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://navlink2023.github.io/' },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://navlink2023.github.io/about' },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="关于我们"
        description="最新网址导航致力于为用户提供最全面、最准确、最实时的网址导航服务。了解我们的使命、核心优势和联系方式，以及用户对我们的真实评价。"
        keywords="最新网址导航,关于我们,网址导航,联系方式,信誉展示"
        canonicalUrl="https://navlink2023.github.io/about"
      />
      <SchemaOrg type="Organization" data={orgSchema} />
      <SchemaOrg type="BreadcrumbList" data={breadcrumbSchema} />

      <Navbar />
      <main className="pt-16">
        <div className="w-full px-4 md:px-6 bg-background-100 border-b border-background-200/70">
          <div className="max-w-4xl mx-auto py-4">
            <Breadcrumb items={[{ label: '关于我们' }]} />
          </div>
        </div>
        <BrandIntroSection />
        <ReputationSection />
        <ContactSection />
      </main>
      <Footer />
    </>
  );
}